import { PCFShadowMap, PerspectiveCamera, Scene, WebGLRenderer } from "three";
import { config } from "../config";
import type { QualitySettings } from "../types/quality";

export interface StageOptions {
  container: HTMLElement;
  settings: QualitySettings;
}

/**
 * Scène, caméra, renderer et redimensionnement. Ne connaît ni le monde,
 * ni les entités : on lui donne une scène à peupler, il l'affiche.
 */
export class Stage {
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly renderer: WebGLRenderer;

  private readonly container: HTMLElement;
  private readonly onResize = (): void => this.resize();
  private readonly onOrientationChange = (): void => {
    // iOS déclenche orientationchange avant d'avoir mis à jour la mise en page :
    // mesurer tout de suite donnerait l'ancienne taille.
    requestAnimationFrame(this.onResize);
  };

  constructor({ container, settings }: StageOptions) {
    this.container = container;
    this.scene = new Scene();
    this.camera = new PerspectiveCamera(
      config.camera.fov,
      container.clientWidth / Math.max(container.clientHeight, 1),
      config.camera.near,
      config.camera.far,
    );

    this.renderer = new WebGLRenderer({
      antialias: settings.antialias,
      powerPreference: "high-performance",
    });
    container.appendChild(this.renderer.domElement);

    this.applyQuality(settings);
    this.resize();

    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onOrientationChange);
  }

  /** `antialias` est ignoré ici : il est figé à la création du contexte WebGL. */
  applyQuality(settings: QualitySettings): void {
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, settings.maxPixelRatio),
    );
    this.renderer.shadowMap.enabled = settings.shadows;
    // PCFSoftShadowMap est déprécié depuis three r18x : le moteur retombait
    // silencieusement sur celui-ci.
    this.renderer.shadowMap.type = PCFShadowMap;
  }

  resize(): void {
    const width = this.container.clientWidth;
    const height = Math.max(this.container.clientHeight, 1);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onOrientationChange);
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.scene.clear();
  }
}
